import { DEAL_MAKER, VEHICLE_FIELDS, DEALER } from '@makemydeal/dr-sales-checkin-types';
import type { WorkflowInputs } from '@makemydeal/dr-sales-checkin-types';

/**
 * The showroom desking and default states, as pure predicates over `WorkflowInputs`.
 *
 * These are the conditions, not the states. Each caller keeps its own vocabulary — the BFF returns a
 * `ShowroomApplicationState`, the client a `WorkflowStatus` — and its own capability guard; only the
 * business rule lives here. That is what stops the two from drifting, as they had: the client used to
 * evaluate SENT_FOR_DESKING ahead of DESKED and ACCEPTED, so a published trade-in deal with an
 * outstanding approval request reported a different state on each side.
 *
 * Precedence is the caller's to enforce, and both callers enforce the same one:
 * ACCEPTED > DESKED > SENT_FOR_DESKING. `isSentForDesking`'s trade-in arm deliberately survives a
 * live published version, so the three are *not* mutually exclusive and the order is load-bearing.
 */

/**
 * The manager-approval half of DESKED, without the published-version requirement. Split out because
 * `isSentForDesking`'s second arm needs exactly this shape, with the *absence* of a published
 * version being the point.
 */
const isApprovedByManager = (inputs: WorkflowInputs): boolean =>
    inputs.approvedByManager && (inputs.versionCreatedByPersona === DEAL_MAKER || inputs.versionCreatedByPersona === DEALER);

/** Buyer accepted a version the manager published back to the showroom. */
export const isAcceptedByBuyer = (inputs: WorkflowInputs): boolean => inputs.hasPublishedVersion && inputs.acceptedByBuyer;

/** Deal's structured version was created by AMD, AMD Elite, or a Tier-1 OEM site (F302128 Part A). */
export const isAmdOriginated = (inputs: WorkflowInputs): boolean => inputs.isAmdOriginated;

/** Manager-published, OR an AMD-originated deal with the PDF-workflow toggle on (F302128 Part A, Story 1). */
export const isDeskedByManager = (inputs: WorkflowInputs): boolean =>
    (inputs.hasPublishedVersion && isApprovedByManager(inputs)) || (inputs.isAmdPdfToggleEnabled && isAmdOriginated(inputs));

/**
 * The manager was asked to desk the deal. Four arms:
 *
 * 1. An explicit approval request with nothing published back yet.
 * 2. A Dealmaker-approved deal whose published version has since been invalidated — the marker is
 *    emptied on every edit after a publish, which is what walks a desked deal back to this stage.
 * 3. A trade-in deal whose approval request is still outstanding, *even with a live published
 *    version*, because the request post-dates that publish. Callers evaluate ACCEPTED and DESKED
 *    first, so this arm only decides deals those two did not claim.
 * 4. A deal that was ever AMD-originated (F302128 Part A), toggle on. `wasEverAmdOriginated` is
 *    durable (sourced from `dealProgressMilestones`), so this stays true even once the deal is
 *    edited and `isAmdOriginated` flips — no "previous status" read needed, same as every other
 *    arm here. Overlaps DESKED exactly the way arm 3 overlaps DESKED/ACCEPTED: while the deal is
 *    still untouched, `isDeskedByManager`'s own AMD arm is true for the same reason, and callers'
 *    priority ordering (DESKED before SENT_FOR_DESKING) resolves it, not this predicate.
 */
export const isSentForDesking = (inputs: WorkflowInputs): boolean =>
    (!inputs.hasPublishedVersion && (inputs.needsManagerApproval || isApprovedByManager(inputs))) ||
    (inputs.hasTradeIn && inputs.needsManagerApproval) ||
    (inputs.isAmdPdfToggleEnabled && inputs.wasEverAmdOriginated);

/**
 * The deal is still missing its selling vehicle. Validation now reports a single `vehicle` field
 * (identity-only — see the BFF's SALES_CHECKIN_DEAL_VALIDATION_CONFIG) when no real vehicle is
 * attached, so this is an exact match on that token.
 *
 * Shared by the MISSING_VEHICLE rule and the default/fallback branch on both sides.
 */
export const isMissingVehicle = (inputs: WorkflowInputs): boolean => inputs.missingFields.includes(VEHICLE_FIELDS);

export const isMissingAnyOf = (inputs: WorkflowInputs, ...sections: string[]): boolean =>
    inputs.missingFields.some((field) => sections.some((section) => field.startsWith(section)));
